/**
 * 圆形元素组件。支持双击进入文字编辑模式。
 */
import { useState, useCallback } from 'react';
import type { CircleElement as CircleElementType } from '@/components/canvas/document/publicTypes';
import type { ElementComponentProps } from '@/components/canvas/elements/types';
import { appendBlurFilter, composeContainerTransform } from '../../group/containerTransform';
import { blurRadius, elementFillCss, firstShadow, firstStroke, shadowToCss } from '@/components/canvas/elements/model';
import { useCanvas } from '@/components/canvas/designer/shell/CanvasContext';
import { ShapeTextEditor } from '../components/ShapeTextEditor';
import { resolveShapeTextStyle } from '../utils/shapeTextStyle';
import { createTextContentData } from '@/components/canvas/elements/text/factory';

export function CircleElement({
  element,
  selected,
  dragOffset = { dx: 0, dy: 0 },
  onSelect,
  onMouseDown,
}: ElementComponentProps<CircleElementType>) {
  const { dx, dy } = dragOffset;
  const { updateElement, setEditingTableCellId } = useCanvas();
  const [isEditing, setIsEditing] = useState(false);

  const circle = element;
  const stroke = firstStroke(circle.appearance);
  const border = stroke?.width
    ? `${stroke.width}px ${stroke.style ?? 'solid'} ${stroke.color ?? 'transparent'}`
    : undefined;
  const shadow = firstShadow(circle.appearance);
  const textStyle = resolveShapeTextStyle(element);

  const containerStyle: React.CSSProperties = {
    position: 'absolute',
    left: element.layout.x + dx,
    top: element.layout.y + dy,
    width: element.size.width,
    height: element.size.height,
    opacity: element.appearance.opacity,
    transform: composeContainerTransform(element),
    boxShadow: shadowToCss(shadow),
    filter: appendBlurFilter(undefined, blurRadius(element)),
    borderRadius: '50%',
    backgroundColor: elementFillCss(circle) ?? '#ddd',
    border,
    overflow: 'hidden',
  };

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
  }, []);

  const handleEditStart = useCallback(() => {
    setEditingTableCellId(element.id);
  }, [element.id, setEditingTableCellId]);

  const handleEditEnd = useCallback(() => {
    setIsEditing(false);
    setEditingTableCellId(null);
  }, [setEditingTableCellId]);

  const handleCommit = useCallback((text: string) => {
    updateElement(element.id, {
      data: {
        ...element.data,
        text: text ? createTextContentData({ content: text }) : undefined,
      },
    });
  }, [element.id, element.data, updateElement]);

  return (
    <div
      style={containerStyle}
      className="pointer-events-auto cursor-inherit"
      data-element-hit
      data-element-id={element.id}
      onClick={(e) => {
        if (isEditing) return;
        e.stopPropagation();
        onSelect(e);
      }}
      onDoubleClick={handleDoubleClick}
      onMouseDown={(e) => {
        if (isEditing) return;
        onMouseDown?.(e, element);
      }}
    >
      <ShapeTextEditor
        text={element.data.text?.content ?? ''}
        textStyle={textStyle}
        isEditing={isEditing}
        onCommit={handleCommit}
        onEditStart={handleEditStart}
        onEditEnd={handleEditEnd}
      />
    </div>
  );
}
